import React, { useEffect, useRef } from 'react';
import {
  Box,
  Form,
  HStack,
  DateField,
  TimePickerField,
  SingleSelectField,
  TextField,
  Text,
  SubmitButton,
  isMobile,
} from '@medely/ui-kit';
import { useFormContext } from '@medely/ui-kit/react-hook-form';
import moment from 'moment-timezone';
import { DateTime } from 'luxon';
import { EDIT_CLOCK_TIMES_REASONS, REASON_OTHER_VALUE } from './constants';
import ErrorAlert from '@mp/components/errorAlert/ErrorAlert';
import { FixedFooterLayout } from '@mp/components/FixedFooterLayout';
import { ResultOf } from '@graphql-typed-document-node/core';
import { parseFragmentData } from '@mp/graphql/generated';
import { useKeyboardLayout } from '@mp/hooks';
import { JobDetailsFragment, useJobForJobDetailsContainer } from '../jobDetailsComponents/graphql';
import { useEditClockTimesMutation } from './mutations';
import { createEditClockTimesSchema, EditClockTimesFormValues } from './schema';
import { getEditClockTimesDefaultValues } from './utils';

const IS_MOBILE = isMobile();

export const EditClockTimesFormContent = ({ jobId }: { jobId: number }) => {
  const { job: jobFromQuery } = useJobForJobDetailsContainer(jobId);
  const job = jobFromQuery ? parseFragmentData(JobDetailsFragment, jobFromQuery) : undefined;
  const timezone = job?.location?.timezone_lookup || moment.tz.guess();
  const { editClockTimes, isLoading, errorMessage } = useEditClockTimesMutation(jobId);

  const handleSubmit = (values: EditClockTimesFormValues) => {
    editClockTimes({ jobId, formValues: values, timezone });
  };

  const schema = createEditClockTimesSchema({
    startsTime: job?.starts_time,
    endsTime: job?.ends_time,
    timezone,
  });

  return (
    <Form key="edit-clock-times" handleSubmit={handleSubmit} schema={schema}>
      <EditClockTimesFormBody job={job} isLoading={isLoading} errorMessage={errorMessage} />
    </Form>
  );
};

function EditClockTimesFormBody({
  job,
  isLoading,
  errorMessage,
}: {
  job: ResultOf<typeof JobDetailsFragment> | undefined;
  isLoading: boolean;
  errorMessage: string | null;
}) {
  const { watch, reset } = useFormContext<{ reason?: string }>();
  const { isKeyboardVisible } = useKeyboardLayout();
  const initializedForJobId = useRef<number | null>(null);

  useEffect(() => {
    if (!job || initializedForJobId.current === job.id) {
      return;
    }
    initializedForJobId.current = job.id;
    const defaults = getEditClockTimesDefaultValues(job);
    reset({
      ...defaults,
      reason: undefined,
      reason_other: undefined,
    } as Parameters<typeof reset>[0]);
  }, [job, reset]);

  const reason = watch('reason');
  const showOtherReasonField = reason === REASON_OTHER_VALUE;

  const tz = job?.location?.timezone_lookup || moment.tz.guess();
  const scheduleStartTime = job?.starts_time
    ? moment.tz(job.starts_time, tz).format('h:mm A')
    : null;
  const scheduleEndTime = job?.ends_time ? moment.tz(job.ends_time, tz).format('h:mm A') : null;

  const clockInMinDate = job?.starts_time
    ? DateTime.fromISO(job.starts_time, { zone: tz }).minus({ hours: 2 })
    : undefined;
  const clockOutMaxDate = job?.ends_time
    ? DateTime.fromISO(job.ends_time, { zone: tz }).plus({ hours: 2 })
    : undefined;

  const renderScheduleHelperText = (type: 'clock-in' | 'clock-out' = 'clock-in') => {
    const label = type === 'clock-in' ? 'start' : 'end';
    const time =
      type === 'clock-in' ? scheduleStartTime ?? '8:00 AM' : scheduleEndTime ?? '8:00 PM';

    return (
      <Box pl={1} pt={0.5}>
        <Text size="s" color="text.secondary">
          {`Schedule ${label} time ${time}`}
        </Text>
      </Box>
    );
  };

  return (
    <FixedFooterLayout
      footerContent={
        !isKeyboardVisible && (
          <Box
            bgColor="surface.paper"
            px={2}
            py={1}
            borderTopWidth={IS_MOBILE ? 1 : 0}
            borderColor="controls.backing+line"
          >
            <SubmitButton fullWidth disabled={isLoading} loading={isLoading}>
              Save
            </SubmitButton>
          </Box>
        )
      }
      height="100%"
    >
      <HStack width="100%" gap={3}>
        <Box flex={1}>
          <DateField
            label="Clock in date*"
            name="approved_clock_in_date"
            labelStyle="stacked"
            minDate={clockInMinDate}
          />
        </Box>
        <Box flex={1}>
          <TimePickerField label="Clock in time" name="approved_clock_in_time" timezone={tz} />
          {renderScheduleHelperText()}
        </Box>
      </HStack>
      <HStack width="100%" gap={3} my={2}>
        <Box flex={1}>
          <DateField
            label="Clock out date*"
            name="approved_clock_out_date"
            labelStyle="stacked"
            maxDate={clockOutMaxDate}
          />
        </Box>
        <Box flex={1}>
          <TimePickerField label="Clock out time" name="approved_clock_out_time" timezone={tz} />
          {renderScheduleHelperText('clock-out')}
        </Box>
      </HStack>
      <SingleSelectField
        variant="bottomSheet"
        name="reason"
        label="Reason"
        options={EDIT_CLOCK_TIMES_REASONS}
      />
      <Box mt={IS_MOBILE ? 0 : 4}>
        {showOtherReasonField && (
          <TextField
            multiline
            labelStyle="stacked"
            name="reason_other"
            placeholder="Tell us why"
            rows={4}
          />
        )}
      </Box>
      {errorMessage && (
        <Box my={2}>
          <ErrorAlert body={errorMessage} title="Error" />
        </Box>
      )}
    </FixedFooterLayout>
  );
}
